import { create } from 'zustand';

/**
 * Solo Timetable Store
 * Manages state for creating/editing weekly timetables in the Solo platform
 */

// Day structure constants
export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// Period structure with times and durations
export const PERIODS = [
  { key: 'period1', label: 'Period 1', time: '9:00-9:45', duration: 45, type: 'activity' },
  { key: 'transition1', label: 'Transition', time: '9:45-9:50', duration: 5, type: 'transition' },
  { key: 'period2', label: 'Period 2', time: '9:50-10:35', duration: 45, type: 'activity' },
  { key: 'break', label: 'Morning Break & Snack', time: '10:35-10:50', duration: 15, type: 'break' },
  { key: 'transition2', label: 'Transition', time: '10:50-10:55', duration: 5, type: 'transition' },
  { key: 'period3', label: 'Period 3', time: '10:55-11:40', duration: 45, type: 'activity' },
  { key: 'transition3', label: 'Transition', time: '11:40-11:45', duration: 5, type: 'transition' },
  { key: 'period4', label: 'Period 4', time: '11:45-12:30', duration: 45, type: 'activity' },
  { key: 'lunch', label: 'Lunch', time: '12:30-13:15', duration: 45, type: 'break' },
  { key: 'transition4', label: 'Transition', time: '13:15-13:20', duration: 5, type: 'transition' },
  { key: 'period5', label: 'Period 5', time: '13:20-14:05', duration: 45, type: 'activity' },
  { key: 'transition5', label: 'Transition', time: '14:05-14:10', duration: 5, type: 'transition' },
  { key: 'period6', label: 'Period 6', time: '14:10-14:45', duration: 35, type: 'activity' }
];

// Default transition activities
export const DEFAULT_TRANSITIONS = [
  { id: 'transition-song', title: 'Transition Song', description: 'Sing a familiar song to signal change', icon: 'musical-note', isTransition: true },
  { id: 'tidy-up', title: 'Tidy Up Time', description: 'Put away materials before moving on', icon: 'archive-box', isTransition: true },
  { id: 'movement-break', title: 'Movement Break', description: 'Quick stretches or movement activity', icon: 'hand-raised', isTransition: true },
  { id: 'countdown', title: 'Countdown Timer', description: 'Visual countdown to prepare for change', icon: 'clock', isTransition: true },
  { id: 'sensory-break', title: 'Sensory Break', description: 'Brief sensory activity to regulate', icon: 'sparkles', isTransition: true }
];

// Domain colors for activity styling
export const DOMAIN_COLORS = {
  'Communication': { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-400', gradient: 'from-blue-500 to-cyan-500' },
  'SEMH': { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-400', gradient: 'from-green-500 to-emerald-500' },
  'Cognition': { bg: 'bg-purple-500/20', border: 'border-purple-500', text: 'text-purple-400', gradient: 'from-purple-500 to-violet-500' },
  'Sensory/Physical': { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', gradient: 'from-orange-500 to-amber-500' },
  'Sensory': { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', gradient: 'from-orange-500 to-amber-500' }
};

/**
 * Initialize an empty timetable grid
 */
function initializeEmptyGrid() {
  const grid = {};
  DAYS.forEach(day => {
    grid[day] = {};
    PERIODS.forEach(period => {
      if (period.type === 'break') {
        grid[day][period.key] = { 
          label: period.label,
          type: 'break'
        };
      } else if (period.type === 'transition') {
        grid[day][period.key] = { 
          activity: null, // Can optionally add a transition activity
          type: 'transition'
        };
      } else {
        grid[day][period.key] = { 
          activity: null,
          type: 'activity'
        };
      }
    });
  });
  return grid;
}

/**
 * Auto-populate timetable with recommended activities
 * Distributes activities based on their recommended frequency
 */
function autoPopulateTimetable(activities, existingGrid = null) {
  const grid = existingGrid ? { ...existingGrid } : initializeEmptyGrid();
  const activityPeriods = PERIODS.filter(p => p.type === 'activity');
  
  if (!activities || activities.length === 0) {
    return grid;
  }

  // Sort activities by score (highest first) and frequency
  const sortedActivities = [...activities].sort((a, b) => {
    const scoreDiff = (b.matchScore || b.score || 0) - (a.matchScore || a.score || 0);
    if (scoreDiff !== 0) return scoreDiff;
    
    const freqA = a.recommendedFrequency?.perWeek || 3;
    const freqB = b.recommendedFrequency?.perWeek || 3;
    return freqB - freqA;
  });

  // Track how many times each activity has been placed
  const activityPlacements = {};
  
  // Distribute activities across the week
  sortedActivities.forEach(activity => {
    const frequency = activity.recommendedFrequency?.perWeek || 3;
    const sessionsPerWeek = Math.min(frequency, 5); // Max once per day
    
    // Select days for this activity (spread evenly)
    const dayIndices = selectSpreadDays(sessionsPerWeek);
    
    dayIndices.forEach(dayIndex => {
      const day = DAYS[dayIndex];
      
      // Find an empty period
      const emptyPeriod = activityPeriods.find(p => 
        grid[day][p.key].activity === null
      );
      
      if (emptyPeriod) {
        grid[day][emptyPeriod.key].activity = {
          id: activity.id,
          title: activity.title,
          description: activity.description,
          duration: activity.recommendedFrequency?.sessionMinutes || 45,
          targetDomains: activity.targetDomains || [],
          matchScore: activity.matchScore || activity.score || 0
        };
        
        activityPlacements[activity.id] = (activityPlacements[activity.id] || 0) + 1;
      }
    });
  });

  // Fill remaining empty slots with lower-scored activities or "free choice"
  DAYS.forEach(day => {
    activityPeriods.forEach(period => {
      if (grid[day][period.key].activity === null) {
        // Find an activity that hasn't been used too much this week
        const underusedActivity = sortedActivities.find(a => {
          const placements = activityPlacements[a.id] || 0;
          const targetFreq = a.recommendedFrequency?.perWeek || 3;
          return placements < targetFreq;
        });
        
        if (underusedActivity) {
          grid[day][period.key].activity = {
            id: underusedActivity.id,
            title: underusedActivity.title,
            description: underusedActivity.description,
            duration: underusedActivity.recommendedFrequency?.sessionMinutes || 45,
            targetDomains: underusedActivity.targetDomains || [],
            matchScore: underusedActivity.matchScore || underusedActivity.score || 0
          };
          activityPlacements[underusedActivity.id] = (activityPlacements[underusedActivity.id] || 0) + 1;
        }
      }
    });
  });

  // Add default transitions
  DAYS.forEach(day => {
    PERIODS.filter(p => p.type === 'transition').forEach((period, idx) => {
      // Cycle through default transitions
      const defaultTransition = DEFAULT_TRANSITIONS[idx % DEFAULT_TRANSITIONS.length];
      grid[day][period.key].activity = { ...defaultTransition };
    });
  });

  return grid;
}

/**
 * Select days spread evenly across the week
 */
function selectSpreadDays(count) {
  if (count >= 5) return [0, 1, 2, 3, 4];
  if (count === 4) return [0, 1, 3, 4];
  if (count === 3) return [0, 2, 4];
  if (count === 2) return [1, 3];
  if (count === 1) return [2]; // Wednesday in the middle
  return [];
}

/**
 * Get the primary domain color for an activity
 */
export function getActivityColor(activity) {
  if (!activity?.targetDomains?.length) {
    return DOMAIN_COLORS['Cognition']; // Default
  }
  const primaryDomain = activity.targetDomains[0];
  return DOMAIN_COLORS[primaryDomain] || DOMAIN_COLORS['Cognition'];
}

export const useSoloTimetableStore = create((set, get) => ({
  // Timetable identification
  currentTimetableId: null,
  profileId: null,
  timetableName: '',
  
  // Grid data
  grid: initializeEmptyGrid(),
  
  // Available activities (from learning profile)
  availableActivities: [],
  
  // Settings
  settings: {
    includeTransitions: true,
    includeSnackTime: true,
    showTimes: true
  },
  
  // UI state
  isLoading: false,
  isSaving: false,
  hasUnsavedChanges: false,
  
  // Editing state
  editingSlot: null, // { day, periodKey }
  
  // ========== ACTIONS ==========
  
  // Initialize from learning profile results
  initializeFromProfile: (profileId, profileName, recommendedActivities) => {
    const grid = autoPopulateTimetable(recommendedActivities);
    set({
      currentTimetableId: null,
      profileId,
      timetableName: `${profileName} - Weekly Timetable`,
      grid,
      availableActivities: recommendedActivities,
      hasUnsavedChanges: true
    });
  },
  
  // Load existing timetable
  loadTimetable: (timetableData) => {
    set({
      currentTimetableId: timetableData.id,
      profileId: timetableData.profileId,
      timetableName: timetableData.name,
      grid: timetableData.grid,
      settings: timetableData.settings || get().settings,
      hasUnsavedChanges: false
    });
  },
  
  // Set available activities (for picker)
  setAvailableActivities: (activities) => {
    set({ availableActivities: activities });
  },
  
  // Set timetable name
  setTimetableName: (name) => {
    set({ timetableName: name, hasUnsavedChanges: true });
  },
  
  // Update a slot's activity
  setSlotActivity: (day, periodKey, activity) => {
    const { grid } = get();
    const newGrid = { ...grid };
    newGrid[day] = { ...newGrid[day] };
    newGrid[day][periodKey] = { 
      ...newGrid[day][periodKey],
      activity: activity ? { ...activity } : null
    };
    set({ grid: newGrid, hasUnsavedChanges: true });
  },
  
  // Clear a slot
  clearSlot: (day, periodKey) => {
    get().setSlotActivity(day, periodKey, null);
  },
  
  // Swap two slots
  swapSlots: (day1, period1, day2, period2) => {
    const { grid } = get();
    const newGrid = { ...grid };
    newGrid[day1] = { ...newGrid[day1] };
    newGrid[day2] = { ...newGrid[day2] };
    
    const temp = newGrid[day1][period1].activity;
    newGrid[day1][period1] = { ...newGrid[day1][period1], activity: newGrid[day2][period2].activity };
    newGrid[day2][period2] = { ...newGrid[day2][period2], activity: temp };
    
    set({ grid: newGrid, hasUnsavedChanges: true });
  },
  
  // Update settings
  updateSettings: (newSettings) => {
    set(state => ({
      settings: { ...state.settings, ...newSettings },
      hasUnsavedChanges: true
    }));
  },
  
  // Toggle transition visibility
  toggleTransitions: () => {
    set(state => ({
      settings: { ...state.settings, includeTransitions: !state.settings.includeTransitions },
      hasUnsavedChanges: true
    }));
  },
  
  // Set editing slot (for modal)
  setEditingSlot: (day, periodKey) => {
    set({ editingSlot: day && periodKey ? { day, periodKey } : null });
  },
  
  // Clear editing slot
  clearEditingSlot: () => {
    set({ editingSlot: null });
  },
  
  // Auto-populate grid with activities
  autoPopulate: (activities = null) => {
    const { availableActivities } = get();
    const activitiesToUse = activities || availableActivities;
    const grid = autoPopulateTimetable(activitiesToUse);
    set({ grid, hasUnsavedChanges: true });
  },
  
  // Clear entire grid
  clearGrid: () => {
    set({ grid: initializeEmptyGrid(), hasUnsavedChanges: true });
  },
  
  // Reset store
  reset: () => {
    set({
      currentTimetableId: null,
      profileId: null,
      timetableName: '',
      grid: initializeEmptyGrid(),
      availableActivities: [],
      settings: {
        includeTransitions: true,
        includeSnackTime: true,
        showTimes: true
      },
      isLoading: false,
      isSaving: false,
      hasUnsavedChanges: false,
      editingSlot: null
    });
  },
  
  // Mark as saved
  markAsSaved: (timetableId = null) => {
    set({ 
      hasUnsavedChanges: false,
      currentTimetableId: timetableId || get().currentTimetableId
    });
  },
  
  // Set loading state
  setLoading: (loading) => set({ isLoading: loading }),
  
  // Set saving state
  setSaving: (saving) => set({ isSaving: saving }),
  
  // ========== COMPUTED / HELPERS ==========
  
  // Get timetable data for saving
  getTimetableData: () => {
    const { timetableName, profileId, grid, settings } = get();
    return {
      name: timetableName,
      profileId,
      grid,
      settings
    };
  },
  
  // Get slot info
  getSlot: (day, periodKey) => {
    const { grid } = get();
    return grid[day]?.[periodKey] || null;
  },
  
  // Check if grid has any activities
  hasActivities: () => {
    const { grid } = get();
    return DAYS.some(day => 
      PERIODS.filter(p => p.type === 'activity').some(period => 
        grid[day][period.key]?.activity !== null
      )
    );
  },
  
  // Get activity count
  getActivityCount: () => {
    const { grid } = get();
    let count = 0;
    DAYS.forEach(day => {
      PERIODS.filter(p => p.type === 'activity').forEach(period => {
        if (grid[day][period.key]?.activity) count++;
      });
    });
    return count;
  },
  
  // Get visible periods based on settings
  getVisiblePeriods: () => {
    const { settings } = get();
    return PERIODS.filter(p => {
      if (p.type === 'transition' && !settings.includeTransitions) return false;
      return true;
    });
  }
}));
